import React from 'react';
import { EmailIcon, ViewIcon } from '@chakra-ui/icons';
import { Box, Flex, Grid, Portal, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { CircleCheckbox } from '../../common/components/CircleCheckbox';
import { ArchiveIcon, DeleteIcon } from '../../styles/custom-icons';
import { NotificationsBulkEdit } from './use-notifications-bulk-edit';

type BulkEditProps = {
  bulkEdit: NotificationsBulkEdit;
};

/** The Edit/Cancel text button which toggles edit mode. */
export function NotificationsEditButton({ bulkEdit }: BulkEditProps) {
  const { t } = useTranslation();

  return (
    <Text
      as="button"
      type="button"
      bg="transparent"
      border={0}
      color="blue.500"
      fontWeight="medium"
      cursor="pointer"
      onClick={bulkEdit.isEditMode ? bulkEdit.handleExitEditMode : bulkEdit.handleEnterEditMode}
      data-testid="notifications-edit-btn"
      px={2}
    >
      {bulkEdit.isEditMode ? t('Common.cancel') : t('Common.edit')}
    </Text>
  );
}

/** The animated "Select All" checkbox row shown while in edit mode. */
export function NotificationsSelectAllCheckbox({ bulkEdit }: BulkEditProps) {
  const { t } = useTranslation();
  const { isEditMode, selectedIds, allVisibleNotificationIds } = bulkEdit;
  const isVisible = isEditMode && allVisibleNotificationIds.length > 0;

  return (
    <Box
      overflow="hidden"
      transition="max-height 0.3s ease, opacity 0.3s ease, margin 0.3s ease"
      maxH={isVisible ? '50px' : '0px'}
      opacity={isVisible ? 1 : 0}
      mb={isVisible ? 3 : 0}
    >
      <Flex align="center">
        <CircleCheckbox
          isChecked={selectedIds.size === allVisibleNotificationIds.length && allVisibleNotificationIds.length > 0}
          isIndeterminate={selectedIds.size > 0 && selectedIds.size < allVisibleNotificationIds.length}
          onChange={bulkEdit.handleSelectAll}
          data-testid="select-all-notifications-checkbox"
        />
        <Text ml={2} fontSize="sm" fontWeight="medium">
          {t('Notifications.select all notifications', { count: allVisibleNotificationIds.length })}
        </Text>
      </Flex>
    </Box>
  );
}

type BulkActionBarProps = BulkEditProps & {
  // Archived notifications can only be deleted or marked read/unread, not archived again
  includeArchiveAction?: boolean;
};

/**
 * The bottom bulk action bar (and its confirmation dialogs) — slides in from the bottom while at least one
 * notification is selected. Rendered in a portal so `position: fixed` anchors to the viewport: the sliding-pane
 * ancestor retains a CSS transform after its page transition, which otherwise turns `fixed` into
 * ancestor-relative positioning and lets the bar scroll away with the page content (most visible on iOS).
 */
export function NotificationsBulkActionBar({ bulkEdit, includeArchiveAction = true }: BulkActionBarProps) {
  const { t } = useTranslation();
  const numActions = includeArchiveAction ? 4 : 3;

  return (
    <Portal>
      <Grid
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        bg="#F7F7F7"
        _dark={{ bg: '#293133' }}
        boxShadow="0 -1px 3px rgba(0,0,0,0.1)"
        px={2}
        pt={3}
        // Keep the buttons clear of the iOS home indicator
        pb="calc(var(--chakra-space-3) + env(safe-area-inset-bottom))"
        templateColumns={`repeat(${numActions}, 1fr)`}
        zIndex={11}
        transform={bulkEdit.isEditMode && bulkEdit.selectedIds.size > 0 ? 'translateY(0)' : 'translateY(100%)'}
        transition="transform 0.3s ease"
      >
        <Flex
          as="button"
          type="button"
          aria-label={t('Common.delete')}
          direction="column"
          align="center"
          justify="center"
          cursor="pointer"
          bg="transparent"
          border="none"
          color="textGrey.500"
          _dark={{ color: 'gray.300' }}
          onClick={bulkEdit.handleDeletePressed}
          data-testid="bulk-delete-btn"
          px={1}
        >
          <DeleteIcon w="16px" h="20px" />
          <Text fontSize="10px" mt={1} textAlign="center" lineHeight="short">
            {t('Common.delete')}
          </Text>
        </Flex>
        {includeArchiveAction && (
          <Flex
            as="button"
            type="button"
            aria-label={t('Notifications.archive')}
            direction="column"
            align="center"
            justify="center"
            cursor="pointer"
            bg="transparent"
            border="none"
            color="textGrey.500"
            _dark={{ color: 'gray.300' }}
            onClick={bulkEdit.handleArchivePressed}
            data-testid="bulk-archive-btn"
            px={1}
          >
            <ArchiveIcon w="20px" h="20px" />
            <Text fontSize="10px" mt={1} textAlign="center" lineHeight="short">
              {t('Notifications.archive')}
            </Text>
          </Flex>
        )}
        <Flex
          as="button"
          type="button"
          aria-label={t('Notifications.mark as read')}
          direction="column"
          align="center"
          justify="center"
          cursor="pointer"
          bg="transparent"
          border="none"
          color="textGrey.500"
          _dark={{ color: 'gray.300' }}
          onClick={bulkEdit.handleBulkMarkAsRead}
          data-testid="bulk-mark-read-btn"
          px={1}
        >
          <ViewIcon w="20px" h="20px" />
          <Text fontSize="10px" mt={1} textAlign="center" lineHeight="short">
            {t('Notifications.mark as read')}
          </Text>
        </Flex>
        <Flex
          as="button"
          type="button"
          aria-label={t('Notifications.mark as unread')}
          direction="column"
          align="center"
          justify="center"
          cursor="pointer"
          bg="transparent"
          border="none"
          color="textGrey.500"
          _dark={{ color: 'gray.300' }}
          onClick={bulkEdit.handleBulkMarkAsUnread}
          data-testid="bulk-mark-unread-btn"
          px={1}
        >
          <EmailIcon w="20px" h="20px" />
          <Text fontSize="10px" mt={1} textAlign="center" lineHeight="short">
            {t('Notifications.mark as unread')}
          </Text>
        </Flex>
      </Grid>

      <AlertDialogModal
        isOpen={bulkEdit.deleteConfirm.isOpen}
        onClose={bulkEdit.deleteConfirm.onClose}
        header={t('Notifications.delete notifications title')}
        subHeader={t('Notifications.delete notifications description')}
        confirmButtonName={t('Common.delete')}
        cancelButtonName={t('Common.cancel')}
        onConfirm={bulkEdit.handleBulkDelete}
        confirmButtonTextColor="#BA3430"
        dialogContentTestId="bulk-delete-confirm-dialog"
      />

      <AlertDialogModal
        isOpen={bulkEdit.archiveConfirm.isOpen}
        onClose={bulkEdit.archiveConfirm.onClose}
        header={t('Notifications.archive notifications title')}
        subHeader={t('Notifications.archive notifications description')}
        confirmButtonName={t('Notifications.archive')}
        cancelButtonName={t('Common.cancel')}
        onConfirm={bulkEdit.handleBulkArchive}
        dialogContentTestId="bulk-archive-confirm-dialog"
      />
    </Portal>
  );
}
